import {
  CheckCircledIcon,
  DownloadIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { Badge, Flex, Skeleton, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { PitchListAvatar } from 'components/common/pitch-lists/list-avatar';
import { SharedListDeleteConfirmation } from 'components/common/pitch-lists/shared-list-delete-warning';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import { CommonTable } from 'components/common/table';
import { TableProvider } from 'components/common/table/context';
import { CommonTooltip } from 'components/common/tooltip';
import { PresetTrainingDialogHoC } from 'components/machine/dialogs/preset-training';
import { GameDataTeamLogo } from 'components/sections/game-data/team-logo';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { PitchListsToolbar } from 'components/sections/pitch-lists/toolbar';
import { PitchesHeader } from 'components/sections/pitches/header';
import env from 'config';
import {
  CheckedContext,
  CheckedProvider,
} from 'contexts/layout/checked.context';
import { MachineContext } from 'contexts/machine.context';
import { SessionEventsContext } from 'contexts/session-events.context';
import { format, parseISO } from 'date-fns';
import lightFormat from 'date-fns/lightFormat';
import { SettingsSection } from 'enums/settings';
import { TABLES } from 'enums/tables';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IMenuAction } from 'interfaces/i-menus';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import {
  LOCAL_DATE_FORMAT,
  LOCAL_PRETTY_DATETIME_FORMAT,
} from 'lib_ts/enums/datetime.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import {
  BuildPriority,
  READ_ONLY_LIST_TYPES,
  TrainingStatus,
} from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { IPitchList, ITrainingPitch } from 'lib_ts/interfaces/pitches';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { MainService } from 'services/main.service';
import { PitchListsService } from 'services/pitch-lists.service';
import { PitchesService } from 'services/pitches.service';
import slugify from 'slugify';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';
import useMiscStore from 'stores/use-misc-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';
import { useVideosStore } from 'stores/use-videos-store';

const ENABLE_TRAIN_LISTS = !env.production;

// notify user every x pitch lists as they are being exported
const PROGRESS_FREQUENCY = 50;

const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

const MAX_PITCH_TYPE_BADGES = 5;

export const PitchListsHoC = () => {
  const current = useAuthStore((s) => s.current);

  const lists = useListLibraryStore((s) => s.lists);
  const toolbarFilters = useListLibraryStore((s) => s.toolbarFilters);

  const { machine } = useContext(MachineContext);

  const visibleLists = useMemo(() => {
    return lists
      .filter((m) =>
        // filter out lists that belong to another team but are accessible to the user
        // e.g. reference / sample / card lists belonging to Trajekt, from pov of customers
        [current.teamID, machine._id, current.userID].includes(m._parent_id)
      )
      .filter((m) =>
        m.name.toLowerCase().includes(toolbarFilters.name.toLowerCase())
      )
      .filter(
        (m) =>
          toolbarFilters.training_status.length === 0 ||
          toolbarFilters.training_status.includes(
            m._mx_summary?.training_status ?? TrainingStatus.Unknown
          )
      )
      .filter(
        (m) =>
          !toolbarFilters.visibility ||
          toolbarFilters.visibility === m._parent_def
      )
      .filter(
        (m) =>
          !toolbarFilters.reference_type ||
          toolbarFilters.reference_type === m.reference_type
      )
      .filter(
        (m) =>
          toolbarFilters.pitch_type.length === 0 ||
          toolbarFilters.pitch_type.every((type) =>
            m._mx_summary?.types.includes(type)
          )
      )
      .filter(
        (m) =>
          toolbarFilters.created.length === 0 ||
          toolbarFilters.created.includes(
            lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT)
          )
      )
      .filter(
        (m) =>
          toolbarFilters.teams.length === 0 ||
          toolbarFilters.teams.includes(m.team?.trim() ?? '')
      );
  }, [current.userID, machine._id, current.teamID, lists, toolbarFilters]);

  return (
    <TableProvider identifier="PitchListsTableProvider">
      <CheckedProvider data={visibleLists}>
        <PitchLists visibleLists={visibleLists} />
      </CheckedProvider>
    </TableProvider>
  );
};

const sortListOwner = (owner: PitchListOwner): number => {
  switch (owner) {
    case PitchListOwner.Team: {
      return 0;
    }

    case PitchListOwner.Machine: {
      return 1;
    }

    case PitchListOwner.User:
    default: {
      return 2;
    }
  }
};

const getDeleteListsTitle = (numLists: number): string => {
  return t('common.delete-x', {
    x: `${numLists} ${
      numLists > 1 ? t('common.pitch-lists') : t('common.pitch-list')
    }?`,
  }).toString();
};

const PitchLists = (props: { visibleLists: IPitchList[] }) => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const readyToTrain = useTrainedMsStore((s) => s.readyToTrain);

  const navigate = useNavigate();
  const { machine, getTrainingPitches } = useContext(MachineContext);

  const [reload, setReload] = useState(Date.now());
  const [loadingSummaryDict, setLoadingSummaryDict] = useState(false);

  const loading = useListLibraryStore((s) => s.loading);

  const { getChecked } = useContext(CheckedContext);
  const { setDialogSettings } = useContext(SessionEventsContext);

  const [dialogTraining, setDialogTraining] = useState<{
    key: number;
    pitches: ITrainingPitch[];
  }>();

  const [dialogDeleteLists, setDialogDeleteLists] = useState<{
    key: number;
    listsIDs: string[];
  }>();

  useEffect(() => {
    setLoadingSummaryDict(true);

    PitchListsService.getInstance()
      .getHashSummaries()
      .then((dict) => useListLibraryStore.setState({ listSummaryDict: dict }))
      .finally(() => setLoadingSummaryDict(false));
  }, [reload, current.teamID, machine.machineID, machine.ball_type]);

  const columns = useMemo<ITypedTableColumn<IPitchList>[]>(
    () => [
      {
        label: 'common.created',
        key: '_created',
        dataType: 'date',
        invisible: true,
      },
      // TODO: Reduce Avatar flickering during filtering/re-render
      {
        label: 'common.name',
        key: 'name',
        classNameFn: () => 'min-width-240px',
        formatFn: (list) => {
          const { name, teamPk, team } = list;
          return (
            <Flex gap={RADIX.FLEX.GAP.MD} align="center">
              <PitchListAvatar list={list} size="3" />
              <Flex
                direction="column"
                justify="center"
                className="text-ellipsis"
              >
                <Text truncate weight="medium">
                  {name}
                </Text>
                <Flex gap={RADIX.FLEX.GAP.XS} align="center">
                  {teamPk && (
                    <GameDataTeamLogo
                      size="16px"
                      teamPK={teamPk}
                      name={team || ''}
                    />
                  )}

                  <Text color={RADIX.COLOR.SECONDARY} truncate>
                    {team}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          );
        },
      },
      {
        label: '',
        key: 'hand',
        align: 'right',
        formatFn: (list) => {
          if (!list.hand) {
            return;
          }

          return <PitcherHandBadge value={list.hand} />;
        },
      },
      {
        label: 'admin.role',
        key: 'role',
        formatFn: (list) => {
          if (!list.position) {
            return <Text>-</Text>;
          }

          return list.position;
        },
      },
      {
        label: 'common.trained-pitches',
        key: '_trained',
        sortRowsFn: (a, b, dir) => {
          const { _mx_summary: aSum } = a;
          const { _mx_summary: bSum } = b;

          if (aSum && bSum) {
            if (aSum.trained_percent === bSum.trained_percent) {
              return aSum.trained > bSum.trained ? -dir : dir;
            }

            return aSum.trained_percent > bSum.trained_percent ? -dir : dir;
          }

          if (aSum) {
            return -dir;
          }

          if (bSum) {
            return dir;
          }

          return dir;
        },
        formatFn: (list) => {
          const color =
            list._mx_summary?.training_status === TrainingStatus.Full
              ? RADIX.COLOR.SUCCESS
              : RADIX.COLOR.NEUTRAL;

          if (!list._mx_summary) {
            return (
              <Skeleton loading={loadingSummaryDict}>
                <Badge color={color}>0/0</Badge>
              </Skeleton>
            );
          }

          const { trained, total } = list._mx_summary;

          return (
            <Skeleton loading={loadingSummaryDict}>
              <Badge color={color}>
                {trained}/{total}
              </Badge>
            </Skeleton>
          );
        },
      },
      {
        label: 'common.pitch-types',
        key: '_types',
        disableSort: true,
        formatFn: (list) => {
          if (loadingSummaryDict) {
            return (
              <Flex gap="1">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} loading={loadingSummaryDict}>
                    <Badge color={RADIX.COLOR.NEUTRAL}>??</Badge>
                  </Skeleton>
                ))}
                <Skeleton loading={loadingSummaryDict}>
                  <Badge color={RADIX.COLOR.NEUTRAL}>??</Badge>
                </Skeleton>
              </Flex>
            );
          }

          if (!list._mx_summary) {
            return null;
          }

          const { _mx_summary } = list;

          const shouldTruncate =
            _mx_summary.types.length > MAX_PITCH_TYPE_BADGES;

          // replace the last pitch type badge with a badge indicating the # of hidden pitch types
          const numVisiblePitchTypes = shouldTruncate
            ? MAX_PITCH_TYPE_BADGES - 1
            : MAX_PITCH_TYPE_BADGES;

          const visiblePitchTypes = _mx_summary.types.slice(
            0,
            numVisiblePitchTypes
          );

          const hiddenPitches = _mx_summary.types.slice(numVisiblePitchTypes);

          return (
            <Flex gap="1">
              {visiblePitchTypes.map((t, i) => (
                <Badge key={i} color={RADIX.COLOR.NEUTRAL}>
                  {t ?? '??'}
                </Badge>
              ))}
              {hiddenPitches.length > 0 && (
                <CommonTooltip
                  trigger={
                    <Badge
                      color={RADIX.COLOR.NEUTRAL}
                    >{`+${hiddenPitches.length}`}</Badge>
                  }
                  text_md={hiddenPitches.join(', ')}
                />
              )}
            </Flex>
          );
        },
      },
      {
        label: 'common.visibility',
        key: '_parent_def',
        sortRowsFn: (a, b, dir) => {
          return (
            dir *
            (sortListOwner(a._parent_def) < sortListOwner(b._parent_def)
              ? -1
              : 1)
          );
        },
        formatFn: (list) => {
          switch (list._parent_def) {
            case PitchListOwner.Team:
              return t('common.team');

            case PitchListOwner.Machine:
              return t('common.machine');

            case PitchListOwner.User:
              return t('common.personal');

            default:
              return t('common.unknown');
          }
        },
      },
      {
        label: 'common.date-created',
        key: '_created',
        invisible: !userRoleFeatureFlags.isSuperAdmin,
        formatFn: (list: IPitchList) =>
          format(parseISO(list._created), LOCAL_PRETTY_DATETIME_FORMAT),
      },
      {
        label: 'common.type',
        labelIcon: <SuperAdminIcon />,
        key: 'type',
        invisible: !userRoleFeatureFlags.isSuperAdmin,
      },
    ],
    [userRoleFeatureFlags, loadingSummaryDict]
  );

  /** exports lists as CSV file */
  const exportListsCSV = (mode: 'checked' | 'all', lists: IPitchList[]) => {
    if (lists.length === 0) {
      NotifyHelper.warning({ message_md: 'Empty export request.' });
      return;
    }

    MainService.getInstance()
      .convertJSONToCSV(lists)
      .then((csvString) => {
        const blob = new Blob([csvString], { type: 'text/csv' });
        MiscHelper.saveAs(
          blob,
          `pitch-lists_${mode}_${lightFormat(
            new Date(),
            LOCAL_DATE_FORMAT
          )}.csv`
        );
      });
  };

  /** exports lists' contents as CSV file */
  const exportListsContentsCSV = async (lists: IPitchList[]) => {
    if (lists.length === 0) {
      NotifyHelper.warning({ message_md: 'Empty export request.' });
      return;
    }

    // get each list's content, save each as a file
    const pitchesService = PitchesService.getInstance();
    const mainService = MainService.getInstance();

    for (let i = 0; i < lists.length; i++) {
      await MiscHelper.sleep(500);

      if (i > 0 && (i + 1) % PROGRESS_FREQUENCY === 0) {
        NotifyHelper.info({
          message_md: `Progress: ${i + 1} / ${lists.length} lists exported...`,
        });
      }

      const list = lists[i];

      const pitches = await pitchesService.search(
        {
          parent: {
            _parent_def: 'pitch-lists',
            _parent_field: 'pitches',
            _parent_id: list._id,
          },
          list: {
            name: null,
            folder: null,
            parent: null,
          },
          filters: null,
          metricsFilters: null,
        },
        0
      );

      if (pitches.length === 0) {
        NotifyHelper.warning({
          message_md: `No pitches found for "${list.name}".`,
        });
        continue;
      }

      const videosDict = ArrayHelper.firstBy(
        useVideosStore.getState().videos,
        '_id'
      );

      const customPitches = pitches.map((p) => {
        return PitchListHelper.convertToCustomExport({
          pitch: p,
          videosDict: videosDict,
          plate_ft: machine.releaseFt,
        });
      });

      const csvString = await mainService.convertJSONToCSV(customPitches);
      const blob = new Blob([csvString], { type: 'text/csv' });
      MiscHelper.saveAs(blob, `${slugify(list.name)}.csv`);
    }

    NotifyHelper.success({
      message_md: `Progress: ${lists.length} lists' contents exported.`,
      delay_ms: 0,
    });
  };

  const isAdmin = current.role === UserRole.admin;

  const handleTrain = async () => {
    const lists = props.visibleLists.filter((v) => getChecked(v._id));

    if (lists.length === 0) {
      NotifyHelper.warning({
        message_md: 'Please select at least one list to train and try again.',
      });
      return;
    }

    NotifyHelper.warning({
      message_md: `Loading data to train ${lists.length} lists, please wait as this may take a while...`,
    });

    const allPitches = (
      await Promise.all(
        lists.map((l) => PitchesService.getInstance().getList(l._id))
      )
    ).flat();

    const untrained = allPitches.filter((p) => !p._mx_tms?._mx_trained);

    if (untrained.length === 0) {
      NotifyHelper.success({
        message_md: 'There are no untrained pitches in the selected lists!',
      });
      return;
    }

    let trainingPitches = await getTrainingPitches(untrained);

    const breakPitches = trainingPitches.filter(
      (p) => p.priority === BuildPriority.Breaks
    );

    const hasSpinPitches = breakPitches.length < allPitches.length;
    const onVision = machine.tracking_device === TrackingDevice.TrajektVision;
    const onTrackman = machine.tracking_device === TrackingDevice.TrackmanB1;

    const onBreakOnlyDevice = (onVision && !isAdmin) || onTrackman;

    // vision currently only supports Break priority pitches
    if (hasSpinPitches && onBreakOnlyDevice) {
      NotifyHelper.warning({
        message_md: onVision
          ? t('common.vision-training-no-spin')
          : t('common.trackman-training-no-spin'),
        buttons: [
          {
            label: 'common.open-settings',
            onClick: () => {
              // close training dialog
              usePitchListStore.getState().closeDialog();

              // show settings dialog
              setDialogSettings({
                identifier: 'BreakOnlyDeviceSettings',
                initialSection: SettingsSection.Machine,
                onClose: () => setDialogSettings(undefined),
              });
            },
            dismissAfterClick: true,
          },
        ],
      });

      // filter out all the Spin/Default priority pitches
      trainingPitches = breakPitches;
    }

    if (trainingPitches.length === 0) {
      NotifyHelper.warning({
        message_md: 'None of the selected pitches can be trained at this time.',
      });
      return;
    }

    setDialogTraining({
      key: Date.now(),
      pitches: trainingPitches,
    });
  };

  const getCheckedMenuActions = (checked: IPitchList[]): IMenuAction[] => {
    const output: IMenuAction[] = [
      {
        prefixIcon: <DownloadIcon />,
        label: 'common.export-pitches',
        onClick: () => exportListsContentsCSV(checked),
      },
      {
        prefixIcon: <DownloadIcon />,
        label: 'common.export-lists',
        onClick: () => exportListsCSV('checked', checked),
      },
      {
        prefixIcon: <CheckCircledIcon />,
        invisible: !ENABLE_TRAIN_LISTS,
        label: 'common.train-pitches',
        disabled: !readyToTrain,
        onClick: () => handleTrain(),
        color: RADIX.COLOR.TRAIN_PITCH,
      },
      {
        prefixIcon: <TrashIcon />,
        label: 'common.delete-lists',
        group: '_1',
        color: RADIX.COLOR.DANGER,
        onClick: () => {
          const deletableLists = checked.filter((list) => {
            if (userRoleFeatureFlags.isSuperAdmin) {
              // super admins can delete anything
              return true;
            }

            if (!list.reference_type) {
              // non-ref lists can be deleted
              return true;
            }

            // regular users cannot delete particular ref list types
            return !READ_ONLY_LIST_TYPES.includes(list.reference_type);
          });

          if (deletableLists.length === 0) {
            NotifyHelper.warning({
              message_md:
                'None of the selected lists can be deleted at this time.',
            });
            return;
          }

          setDialogDeleteLists({
            key: Date.now(),
            listsIDs: deletableLists.map((c) => c._id),
          });
        },
      },
    ];

    return output;
  };

  const pagination: ITablePageable = {
    paginationID: PaginationID.PitchLists,
    total: props.visibleLists.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: '_created',
      dir: 1,
    },
  };

  const select: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: (m: IPitchList | undefined) => {
      if (!m) {
        return;
      }

      navigate(paths.pitches.list(m._id));
    },
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    // todo: kind of weird deviation from rules here, since the checkboxes interact with the actions menu at the top rather than a checked menu at the bottom
    checkedActions: getCheckedMenuActions,
  };

  return (
    <ErrorBoundary identifier="PitchLists">
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        header={
          <PitchesHeader
            extraActions={[
              {
                prefixIcon: <DownloadIcon />,
                label: 'common.export-all-pitches',
                onClick: () => exportListsContentsCSV(props.visibleLists),
              },
              {
                prefixIcon: <DownloadIcon />,
                label: 'common.export-all-lists',
                onClick: () => exportListsCSV('all', props.visibleLists),
              },
            ]}
          />
        }
        table={
          <CommonTable
            identifier="PitchListsTable"
            dataTestID="PitchLists"
            displayColumns={columns}
            displayData={props.visibleLists}
            toolbarContent={<PitchListsToolbar />}
            enableListener={!anyOpenDialogs}
            rowClassNameFn={() => 'tall-3'}
            loading={loading}
            {...pagination}
            {...checkable}
            {...select}
            {...sort}
            vFlex
          />
        }
      />

      {dialogTraining && (
        <PresetTrainingDialogHoC
          identifier="PLS-PT-TrainingDialog"
          pitches={dialogTraining.pitches}
          onClose={async () => {
            // trigger the listener to wait for machine to stop firing > lower the machine if necessary
            useTrainedMsStore.setState({
              lastTrainingEnded: Date.now(),
            });

            const bsHashes = dialogTraining.pitches.map((p) => p.bs_hash) ?? [];

            if (bsHashes.length > 0) {
              await useTrainedMsStore
                .getState()
                .buildTmsDict(
                  machine.machineID,
                  machine.ball_type,
                  machine.last_hardware_changed,
                  true
                );

              setReload(Date.now());
            }

            setDialogTraining(undefined);
          }}
        />
      )}

      {dialogDeleteLists && (
        <CommonConfirmationDialog
          key={dialogDeleteLists.key}
          identifier="DeleteListsDialog"
          title={getDeleteListsTitle(dialogDeleteLists.listsIDs.length)}
          content={
            <SharedListDeleteConfirmation
              lists={props.visibleLists.filter((l) =>
                dialogDeleteLists.listsIDs.includes(l._id)
              )}
            />
          }
          action={{
            label: 'common.delete',
            color: RADIX.COLOR.DANGER,
            loading,
            onClick: () =>
              useListLibraryStore
                .getState()
                .deleteLists(dialogDeleteLists.listsIDs)
                .then((success) => {
                  if (success) {
                    setDialogDeleteLists(undefined);
                  }
                }),
          }}
          onClose={() => setDialogDeleteLists(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
